import type { ApiTypes } from "@fluid-app/fluid";

import type { CartData } from "../../cart-widget/mockData";
import type { MediaItem, Product } from "../types/contexts";

/**
 * Transform library/playlist API response to MediaItem array
 * @param libraryResponse The library response from the API
 * @returns Array of MediaItem objects
 */
export function transformLibraryToMediaItems(
  libraryResponse: ApiTypes["library-show"]["response"],
): MediaItem[] {
  const library = libraryResponse.library;

  const mediaItems: MediaItem[] = [];
  const products = library.products;

  for (const libraryItem of library.library_items) {
    try {
      const mediaItem = transformLibraryItemToMediaItem(libraryItem, products);
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    } catch (error) {
      console.error("Error transforming library item:", error);
    }
  }

  return mediaItems;
}

/**
 * Transform library/playlist API response to MediaItem array and CartData
 * @param libraryResponse The library response from the API
 * @returns Object containing MediaItem array and CartData
 */
export function transformLibraryToMediaItemsWithCart(
  libraryResponse: ApiTypes["library-show"]["response"],
): { mediaItems: MediaItem[]; cartData: CartData } {
  const library = libraryResponse.library;
  const mediaItems: MediaItem[] = [];
  const products = library.products;

  for (const libraryItem of library.library_items) {
    try {
      const mediaItem = transformLibraryItemToMediaItem(libraryItem, products);
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    } catch (error) {
      console.error("Error transforming library item:", error);
    }
  }

  // Transform API products directly to CartData
  const cartData = transformApiProductsToCartData(products);

  return { mediaItems, cartData };
}

/**
 * Transform single media API response to MediaItem
 * @param mediaResponse The media response from the API
 * @returns MediaItem object
 */
export function transformMediaToMediaItem(
  mediaResponse: ApiTypes["media-show"]["response"],
): MediaItem {
  const media = mediaResponse.media;

  const products = media.products;

  // Transform products to Product format
  const transformedProducts: Product[] = products.map((product) => ({
    id: product.id,
    variant_id: product.id, // Use product ID as variant ID for now
    name: product.title,
    description: "", // API doesn't provide description, leave empty
    price:
      typeof product.price === "string"
        ? parseFloat(product.price)
        : (product.price ?? 0),
    image: product.image_url ?? product.image_path ?? "",
  }));

  // Determine media type based on media_type and media_format
  const mediaType = getMediaTypeFromAPI(media.media_type, media.media_format);

  // Get appropriate URL based on media type
  const mediaUrl = getMediaUrlFromAPI(media, mediaType);

  // Get thumbnail URL
  const thumbnailUrl = media.image_url;

  return {
    id: media.id,
    type: mediaType,
    url: mediaUrl,
    title: media.title,
    description: media.description,
    thumbnail: thumbnailUrl ?? "",
    products: transformedProducts,
  };
}

/**
 * Transform single media API response to MediaItem and return both MediaItem and CartData
 * @param mediaResponse The media response from the API
 * @returns Object containing MediaItem and CartData
 */
export function transformMediaToMediaItemWithCart(
  mediaResponse: ApiTypes["media-show"]["response"],
): { mediaItem: MediaItem; cartData: CartData } {
  const media = mediaResponse.media;
  const products = media.products;

  // Transform products to Product format for MediaItem
  const transformedProducts: Product[] = products.map((product) => ({
    id: product.id,
    variant_id: product.id,
    name: product.title,
    description: "",
    price:
      typeof product.price === "string"
        ? parseFloat(product.price)
        : (product.price ?? 0),
    image: product.image_url ?? product.image_path ?? "",
  }));

  // Transform API products directly to CartData
  const cartData = transformApiProductsToCartData(products);

  // Determine media type based on media_type and media_format
  const mediaType = getMediaTypeFromAPI(media.media_type, media.media_format);

  // Get appropriate URL based on media type
  const mediaUrl = getMediaUrlFromAPI(media, mediaType);

  // Get thumbnail URL
  const thumbnailUrl = media.image_url;

  const mediaItem: MediaItem = {
    id: media.id,
    type: mediaType,
    url: mediaUrl,
    title: media.title,
    description: media.description,
    thumbnail: thumbnailUrl ?? "",
    products: transformedProducts,
  };

  return { mediaItem, cartData };
}

/**
 * Transform a single library item to MediaItem
 * @param libraryItem The library item from the API
 * @param products Available products from the library
 * @returns MediaItem object or null if transformation fails
 */
function transformLibraryItemToMediaItem(
  libraryItem: ApiTypes["library-show"]["response"]["library"]["library_items"][0],
  products: ApiTypes["library-show"]["response"]["library"]["products"],
): MediaItem | null {
  try {
    // Extract media data from library item
    const item = libraryItem.item as Record<string, unknown>;

    // Determine media type
    const mediaType = getMediaTypeFromAPI(
      item.media_type as string,
      item.media_format as string,
    );

    // Get media URL
    const mediaUrl = getMediaUrlFromItem(item, mediaType);

    // Get thumbnail URL
    const thumbnailUrl = item.image_url;

    // Transform products to Product format
    const transformedProducts: Product[] = products.map((product) => ({
      id: product.id,
      variant_id: product.id, // Use product ID as variant ID for now
      name: product.title,
      description: "", // API doesn't provide description, leave empty
      price:
        typeof product.price === "string"
          ? parseFloat(product.price)
          : (product.price ?? 0),
      image: product.image_url ?? product.image_path ?? "",
    }));

    return {
      id: item.id as number,
      type: mediaType,
      url: mediaUrl,
      title: item.title as string,
      description: item.description as string,
      thumbnail: thumbnailUrl as string,
      products: transformedProducts,
    };
  } catch (error) {
    console.error("Error transforming library item:", error);
    return null;
  }
}

/**
 * Get media type from API media_type and media_format
 * @param mediaType The media_type from API
 * @param mediaFormat The media_format from API
 * @returns MediaItem type
 */
function getMediaTypeFromAPI(
  mediaType?: string | null,
  mediaFormat?: string | null,
): MediaItem["type"] {
  if (!mediaType) return "video"; // Default to video

  const type = mediaType.toLowerCase();
  const format = mediaFormat?.toLowerCase();

  switch (type) {
    case "video":
      return "video";
    case "image":
    case "share": // "share" type with image format should be treated as image
      return "image";
    case "document":
      if (format?.includes("pdf")) return "pdf";
      return "website"; // Default for documents
    case "website":
      return "website";
    default:
      // For unknown types, try to determine from format or fallback to image if we have image_url
      if (
        format?.includes("png") ||
        format?.includes("jpg") ||
        format?.includes("jpeg") ||
        format?.includes("gif") ||
        format?.includes("webp")
      ) {
        return "image";
      }
      return "video"; // Default fallback
  }
}

/**
 * Get media URL from API media object
 * @param media The media object from API
 * @param mediaType The determined media type
 * @returns Media URL string
 */
function getMediaUrlFromAPI(
  media: ApiTypes["media-show"]["response"]["media"],
  mediaType: MediaItem["type"],
): string {
  switch (mediaType) {
    case "video":
      return media.video_url ?? "";
    case "image":
      // For images, prioritize image_url, but also check video_url as fallback
      return media.image_url ?? media.video_url ?? "";
    case "pdf":
      return media.pdf_url ?? "";
    case "website":
      // For website type, we might need to construct a URL
      // or use a specific field - this depends on your API structure
      return media.video_url ?? media.image_url ?? "";
    default:
      // For unknown types, try image_url first, then video_url
      return media.image_url ?? media.video_url ?? "";
  }
}

/**
 * Get media URL from library item
 * @param item The library item object
 * @param mediaType The determined media type
 * @returns Media URL string
 */
function getMediaUrlFromItem(
  item: Record<string, unknown>,
  mediaType: MediaItem["type"],
): string {
  switch (mediaType) {
    case "video":
      return item.video_url as string;
    case "image":
      // For images, prioritize image_url, but also check video_url as fallback
      return item.image_url as string;
    case "pdf":
      return item.pdf_url as string;
    case "website":
      return item.url as string;
    default:
      // For unknown types, try image_url first, then video_url, then url
      return item.image_url as string;
  }
}

/**
 * Transform products from MediaItem format to CartData format
 * @param products Array of products from MediaItem
 * @returns CartData with items transformed from products
 */
export function transformProductsToCartData(products: Product[]): CartData {
  const cartItems = products.map((product, index) => ({
    id: index + 1,
    variant: {
      id: product.variant_id,
      image_url: product.image,
      price_in_currency: `$${product.price.toFixed(2)}`,
      image_path: product.image,
      price: `$${product.price.toFixed(2)}`,
      primary_image: product.image,
      sku: `SKU${product.id}`,
      title: product.name,
    },
    quantity: 1,
    product_title: product.name,
    price_in_currency: `$${product.price.toFixed(2)}`,
    product: {
      image_url: product.image,
    },
  }));

  const subTotal = products.reduce((acc, product) => acc + product.price, 0);

  return {
    id: Date.now(), // Generate a unique cart ID
    items: cartItems,
    sub_total_in_currency: `$${subTotal.toFixed(2)}`,
    recurring: [],
  };
}

/**
 * Transform API products directly to CartData format
 * @param apiProducts Array of products from the API response
 * @returns CartData with items transformed from API products
 */
export function transformApiProductsToCartData(
  apiProducts: ApiTypes["media-show"]["response"]["media"]["products"],
): CartData {
  const cartItems = apiProducts.map((product, index) => ({
    id: index + 1,
    variant: {
      id: product.id,
      image_url: product.image_url ?? "",
      price_in_currency: product.price_in_currency || "$0.00",
      image_path: product.image_path,
      price: product.price_in_currency || "$0.00",
      primary_image: product.image_url ?? "",
      sku: product.sku ?? `SKU${product.id}`,
      title: product.title,
    },
    quantity: 1,
    product_title: product.title,
    price_in_currency: product.price_in_currency || "$0.00",
    product: {
      image_url: product.image_url ?? "",
    },
  }));

  const subTotal = apiProducts.reduce((acc, product) => {
    const price =
      typeof product.price === "string"
        ? parseFloat(product.price)
        : (product.price ?? 0);
    return acc + price;
  }, 0);

  return {
    id: Date.now(), // Generate a unique cart ID
    items: cartItems,
    sub_total_in_currency: `$${subTotal.toFixed(2)}`,
    recurring: [],
  };
}
